import { useTranslation } from "react-i18next";
import * as React from "react";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Mail, Eye, AlertTriangle, Send } from "lucide-react";

interface SendForApprovalConfirmDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onConfirm: () => void;
  customerEmail: string;
  customerName: string;
  quoteNumber?: string;
  isLoading?: boolean;
}

export const SendForApprovalConfirmDialog: React.FC<
  SendForApprovalConfirmDialogProps
> = ({
  open,
  onOpenChange,
  onConfirm,
  customerEmail,
  customerName,
  quoteNumber,
  isLoading = false,
}) => {
  const { t } = useTranslation();
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[425px]">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Mail className="h-5 w-5 text-blue-600" />
            {t("components.sendForApprovalConfirmDialog.sendQuoteForApproval")}
          </DialogTitle>
          <DialogDescription className="space-y-3 pt-2">
            <div className="flex items-start gap-3 p-3 bg-blue-50 rounded-lg border border-blue-200">
              <AlertTriangle className="h-5 w-5 text-blue-600 mt-0.5 flex-shrink-0" />
              <div className="space-y-2">
                <p className="text-sm font-medium text-blue-900">
                  {t("components.sendForApprovalConfirmDialog.youReAboutTo")}
                </p>
                <div className="space-y-1 text-sm text-blue-800">
                  <p>
                    <strong>{t("components.sendForApprovalConfirmDialog.customer")}</strong> {customerName}
                  </p>
                  <p>
                    <strong>{t("components.sendForApprovalConfirmDialog.email")}</strong> {customerEmail}
                  </p>
                  {quoteNumber && (
                    <p>
                      <strong>{t("components.sendForApprovalConfirmDialog.quote")}</strong> {quoteNumber}
                    </p>
                  )}
                </div>
              </div>
            </div>

            <div className="flex items-start gap-3 p-3 bg-amber-50 rounded-lg border border-amber-200">
              <Eye className="h-5 w-5 text-amber-600 mt-0.5 flex-shrink-0" />
              <div className="text-sm text-amber-800">
                <p className="font-medium mb-1">{t("components.sendForApprovalConfirmDialog.important")}</p>
                <p>
                  {t("components.sendForApprovalConfirmDialog.theCustomerWillSee")}
                </p>
              </div>
            </div>
          </DialogDescription>
        </DialogHeader>

        <DialogFooter className="flex-col-reverse sm:flex-row gap-2">
          <Button
            variant="outline"
            onClick={() => onOpenChange(false)}
            disabled={isLoading}
          >
            {t("components.sendForApprovalConfirmDialog.back")}
          </Button>
          <Button
            onClick={onConfirm}
            disabled={isLoading}
            className="bg-blue-600 hover:bg-blue-700"
          >
            <Send className="h-4 w-4 mr-2" />
            {isLoading ? t("components.sendForApprovalConfirmDialog.sending") : t("components.sendForApprovalConfirmDialog.sendForApproval")}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
